'use client';
// Relevé de température des enceintes — TEMP-1.
//
// ── Ce que cet écran refuse de faire ────────────────────────────────────────
//
//  · PRÉTENDRE À UNE SURVEILLANCE CONTINUE. Le suivi est manuel, deux fois par
//    jour, au thermomètre. Entre deux passages il ne se passe RIEN — sauf si le
//    thermomètre est un mini/maxi. Chaque relevé porte donc sa COUVERTURE, et
//    « instantanée seule » est affiché en clair, avec sa conséquence écrite :
//    une excursion entre les deux passages resterait invisible.
//
//  · CACHER UN PASSAGE MANQUANT. Ce qui aurait dû être relevé et ne l'a pas été
//    est compté, daté et affiché à côté de ce qui l'a été. Un registre qui
//    n'afficherait que les relevés faits se lirait « tout a été relevé ».
//
//  · OMETTRE UNE ENCEINTE EN SILENCE. Une enceinte non relevée est
//    EXPLICITEMENT dite non relevée, avec un motif obligatoire. La laisser vide
//    ne l'enregistre pas et ne solde pas son passage : le manquant demeure, et
//    l'écran le dit à l'enregistrement.
//
//  · INVENTER UNE PLAGE. Trois enceintes n'en portent aucune et `CONG-03` est
//    laissée vide à dessein (combiné, compartiment surveillé non établi). La
//    saisie fonctionne quand même, et la ligne porte l'avertissement : la
//    valeur est enregistrée, le franchissement n'est PAS jugeable.
//
//  · CRÉER UNE NON-CONFORMITÉ TOUT SEUL. Un franchissement PROPOSE, avec son
//    motif, son ampleur et la durée maximale de l'excursion. La décision est
//    signée par quelqu'un. Même patron que le contrôle interne (CQI-1).
//
// ── Et ce qu'il annonce sans détour ─────────────────────────────────────────
//
// Aucune sonde n'est installée. La provenance `sonde` existe dans le modèle
// pour que l'ingestion future écrive dans ce registre sans le réécrire ; aucune
// n'est produite ici, et l'écran ne fait pas semblant du contraire.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BoutonJournal } from '@/components/BoutonJournal';
import { useGed, userName } from '@/lib/store';
import { useRemonteEnHaut,
  I, card, inputStyle, EmptyState, Skeleton, RefISO, useBusy, ConfirmButton, Selecteur } from '@/components/ui';
import { H1, lbl, pill } from './commun';
import type {
  GedTempEtat, GedTempTournee, GedTempStatut, GedTempCouverture,
  GedTempLigneTournee, GedTempLignePropos,
} from '@/lib/types';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { aujourdhuiIso, fmtDate } from '@/lib/meta';
import { peutGererParc } from '@/lib/droits-parc';
const STATUT_STYLE: Record<GedTempStatut, { bg: string; fg: string; label: string }> = {
  conforme: { bg: 'var(--green-50)', fg: 'var(--green-600)', label: 'Dans la plage' },
  franchissement: { bg: 'var(--red-50)', fg: 'var(--red-600)', label: 'Hors plage' },
  non_jugeable: { bg: 'var(--gray-100)', fg: 'var(--text-muted)', label: 'Non jugeable' },
  non_releve: { bg: 'var(--amber-50)', fg: 'var(--amber-600)', label: 'Non relevée' },
};
const COUV_STYLE: Record<GedTempCouverture, { bg: string; fg: string; label: string }> = {
  extremes: { bg: 'var(--green-50)', fg: 'var(--green-600)', label: 'Mini/maxi' },
  instantane_seul: { bg: 'var(--amber-50)', fg: 'var(--amber-600)', label: 'Instantanée seule' },
  absente: { bg: 'var(--gray-100)', fg: 'var(--text-muted)', label: 'Aucune' },
};

const encart: React.CSSProperties = {
  background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)',
  borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5,
  color: 'var(--text-body)', lineHeight: 1.5,
};
const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 12.5 };

/** ⚠️ `0` est une température, pas une absence. Toute la mise en forme passe
 *  par une comparaison explicite à `null` — un `v ? … : '—'` afficherait « — »
 *  pour 0 °C, c'est-à-dire ferait disparaître une valeur d'un congélateur en
 *  cours de dégivrage, exactement le relevé qu'il ne faut pas perdre. */
const degre = (v: number | null | undefined) =>
  (v === null || v === undefined ? '—' : `${String(v).replace('.', ',')} °C`);

function Tuile({ n, label, aide, ton }: { n: number | string; label: string; aide: string; ton?: 'sourd' | 'alerte' | 'attention' }) {
  const couleur = ton === 'alerte' ? 'var(--red-600)'
    : ton === 'attention' ? 'var(--amber-600)'
      : ton === 'sourd' ? 'var(--text-muted)' : 'var(--text-strong)';
  return (
    <div title={aide} style={{ ...card, padding: '10px 13px', display: 'grid', gap: 2, cursor: 'help', minWidth: 118 }}>
      <span style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1, color: couleur }}>{n}</span>
      <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
        {label}
      </span>
    </div>
  );
}

/* ══ La TOURNÉE ════════════════════════════════════════════════════════════
 *
 * Une liste, pas quatorze formulaires. C'est un geste fait deux fois par jour,
 * debout, devant les enceintes : ouvrir, remplir et valider quatorze fiches
 * serait abandonné en une semaine, et le registre le plus complet du monde ne
 * vaut rien s'il n'est pas tenu. */

type Saisie = {
  valeur: string; valeurMin: string; valeurMax: string;
  nonReleve: boolean; motifNonReleve: string; reinitialise: boolean; commentaire: string;
};
const SAISIE_VIDE: Saisie = {
  valeur: '', valeurMin: '', valeurMax: '', nonReleve: false,
  motifNonReleve: '', reinitialise: false, commentaire: '',
};
const depuisLigne = (l: GedTempLigneTournee): Saisie => ({
  valeur: l.deja?.valeur !== null && l.deja?.valeur !== undefined ? String(l.deja.valeur) : '',
  valeurMin: l.deja?.valeurMin !== null && l.deja?.valeurMin !== undefined ? String(l.deja.valeurMin) : '',
  valeurMax: l.deja?.valeurMax !== null && l.deja?.valeurMax !== undefined ? String(l.deja.valeurMax) : '',
  nonReleve: l.deja?.nonReleve === true,
  motifNonReleve: '', reinitialise: false, commentaire: '',
});

function LigneTournee({ l, s, onChange }: {
  l: GedTempLigneTournee; s: Saisie; onChange: (s: Saisie) => void;
}) {
  const champ = (k: 'valeur' | 'valeurMin' | 'valeurMax', place: string) => (
    <input
      type="text" inputMode="decimal"
      placeholder={place} aria-label={`${l.code}${l.pointLibelle ? ` ${l.pointLibelle}` : ''} — ${place}`}
      value={s[k]} disabled={s.nonReleve}
      onChange={(e) => onChange({ ...s, [k]: e.target.value })}
      style={{ ...inputStyle, width: 84, textAlign: 'end', opacity: s.nonReleve ? 0.5 : 1 }}
    />
  );
  return (
    <div style={{
      display: 'grid', gap: 8, padding: '10px 12px',
      borderTop: '1px solid var(--border-subtle)',
      background: s.nonReleve ? 'var(--amber-50)' : 'transparent',
    }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 210 }}>
          <strong style={{ fontSize: 13, color: 'var(--text-strong)' }}>{l.code}</strong>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}> — {l.nom}</span>
          {/* Le compartiment n'est nommé QUE sur une enceinte qui en déclare
              plusieurs : sur un réfrigérateur ordinaire, la ligne est celle
              d'avant, sans un mot de plus. */}
          {l.pointLibelle ? (
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-body)' }}>{l.pointLibelle}</div>
          ) : null}
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            {l.localisation || 'localisation non renseignée'}
          </div>
        </div>
        <span style={{ ...mono, minWidth: 108, color: l.plage ? 'var(--text-body)' : 'var(--amber-600)' }}>
          {l.plage ? `${l.plage.min} à ${l.plage.max} °C` : 'plage non fixée'}
        </span>
        {champ('valeur', 'Actuelle')}
        {champ('valeurMin', 'Mini')}
        {champ('valeurMax', 'Maxi')}
        <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 12.5 }}>
          <input
            type="checkbox" checked={s.reinitialise} disabled={s.nonReleve}
            onChange={(e) => onChange({ ...s, reinitialise: e.target.checked })}
          />
          mini/maxi réinitialisé
        </label>
        <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 12.5, fontWeight: 700 }}>
          <input
            type="checkbox" checked={s.nonReleve}
            onChange={(e) => onChange({ ...s, nonReleve: e.target.checked })}
          />
          non relevée
        </label>
        {l.deja && <span style={{ fontSize: 12.5, color: 'var(--green-600)' }}>déjà saisi — la validation corrige</span>}
      </div>

      {s.nonReleve && (
        <input
          type="text" placeholder="Motif obligatoire — pourquoi cette enceinte n’a pas été relevée"
          value={s.motifNonReleve} onChange={(e) => onChange({ ...s, motifNonReleve: e.target.value })}
          style={{ ...inputStyle, width: '100%' }}
        />
      )}
      {l.avertissement && (
        <div style={{ ...encart }}>⚠️ {l.avertissement}</div>
      )}
      {!s.nonReleve && s.valeur.trim() !== '' && s.valeurMin.trim() === '' && s.valeurMax.trim() === '' && (
        <div style={{ fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.5 }}>
          Sans les extrêmes, la période écoulée depuis le passage précédent n’est pas couverte :
          une excursion entre les deux passages resterait invisible.
        </div>
      )}
      {l.precedent && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Passage précédent — {l.precedent.date} ({l.precedent.creneau}) :
          {' '}{degre(l.precedent.valeur)} · mini {degre(l.precedent.valeurMin)} · maxi {degre(l.precedent.valeurMax)}
          {' '}· {STATUT_STYLE[l.precedent.statut]?.label ?? l.precedent.statut}
        </div>
      )}
    </div>
  );
}

function Tournee({ onEnregistre }: { onEnregistre: () => void }) {
  const smq = useGed((s) => s.smq);
  const showToast = useGed((s) => s.showToast);
  const [t, setT] = useState<GedTempTournee | null>(null);
  const [saisies, setSaisies] = useState<Record<string, Saisie>>({});
  const [date, setDate] = useState('');
  const [creneau, setCreneau] = useState('');
  // Lot 5 (11/08/2026) — l'heure réelle de lecture part de l'heure COURANTE :
  // dans neuf tournées sur dix c'est la bonne, et elle reste corrigeable.
  const [heure, setHeure] = useState(() => new Date().toTimeString().slice(0, 5));
  const [erreur, setErreur] = useState('');
  const { estOccupe, run } = useBusy();

  /** La lecture SEULE — aucun état touché ici, pour que l'effet de montage
   *  puisse l'appeler sans poser d'état de façon synchrone. */
  const lire = useCallback(async (d?: string, c?: string): Promise<GedTempTournee | null> => {
    const q = new URLSearchParams();
    if (d) q.set('date', d);
    if (c) q.set('creneau', c);
    const r = await fetch(`/api/ged/ged/releves-temp/tournee?${q.toString()}`, { credentials: 'include' });
    return r.ok ? ((await r.json()) as GedTempTournee) : null;
  }, []);

  const appliquer = useCallback((data: GedTempTournee) => {
    setT(data);
    setDate(data.date);
    setCreneau(data.creneau.code);
    setSaisies(Object.fromEntries(data.lignes.map((l) => [l.cle, depuisLigne(l)])));
    setErreur('');
  }, []);

  const charger = useCallback(async (d?: string, c?: string) => {
    const data = await lire(d, c);
    if (data) appliquer(data);
    // ⚠️ Jamais de tournée vide en cas d'échec : une liste vide se lirait
    // « aucune enceinte à relever », c'est-à-dire « rien à faire ».
    else setErreur('La tournée n’a pas pu être chargée. Ne rien conclure de cet écran.');
  }, [lire, appliquer]);

  useEffect(() => {
    let vivant = true;
    void lire().then((d) => {
      if (!vivant) return;
      if (d) appliquer(d);
      else setErreur('La tournée n’a pas pu être chargée. Ne rien conclure de cet écran.');
    });
    return () => { vivant = false; };
  }, [lire, appliquer]);

  if (erreur) {
    return (
      <EmptyState titre="La tournée n’a pas pu être chargée." indice="Ne rien conclure de cet écran tant que la lecture n’a pas abouti."
        action={<Bouton taille="sm" type="button" onClick={() => { setErreur(''); void charger(date || undefined, creneau || undefined); }}>Réessayer</Bouton>} />
    );
  }
  if (!t) return <div style={card}><Skeleton rows={4} /></div>;
  if (!t.lignes.length) {
    return (
      <EmptyState
        titre="Aucune enceinte n’est inscrite à la tournée"
        indice="Le périmètre est DÉCLARÉ sur la fiche de chaque équipement (bloc métrologie, « Relevé de température »), jamais déduit d’un libellé. Tant qu’aucune enceinte n’y est inscrite, ce registre ne surveille rien — et « 0 franchissement » ne voudrait rien dire."
      />
    );
  }

  const enregistrer = () => run('tournee', async () => {
    const lignes = t.lignes.map((l) => {
      const s = saisies[l.cle] ?? SAISIE_VIDE;
      return {
        code: l.code,
        // ⚠️ Le point de surveillance fait partie de l'identité du relevé : sans
        // lui, les deux compartiments de `CONG-03` s'écraseraient l'un l'autre.
        // Vide sur les treize autres enceintes, où la ligne est celle d'avant.
        pointCode: l.pointCode,
        valeur: s.valeur.trim(), valeurMin: s.valeurMin.trim(), valeurMax: s.valeurMax.trim(),
        nonReleve: s.nonReleve, motifNonReleve: s.motifNonReleve,
        reinitialise: s.reinitialise, commentaire: s.commentaire,
        provenance: 'manuel',
      };
    });
    const ok = await smq('ged/releves-temp/tournee', { date, creneau, heure, lignes });
    if (ok) {
      const vides = lignes.filter((l) => !l.nonReleve && !l.valeur && !l.valeurMin && !l.valeurMax).length;
      showToast(vides
        ? `Tournée enregistrée. ${vides} point(s) de relevé sans valeur et sans déclaration « non relevée » : leur passage reste MANQUANT.`
        : 'Tournée enregistrée.');
      await charger(date, creneau);
      onEnregistre();
    }
  });

  const remplies = t.lignes.filter((l) => {
    const s = saisies[l.cle] ?? SAISIE_VIDE;
    return s.nonReleve || s.valeur.trim() || s.valeurMin.trim() || s.valeurMax.trim();
  }).length;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ ...card, padding: 13, display: 'flex', gap: 14, alignItems: 'end', flexWrap: 'wrap' }}>
        <div>
          <span style={lbl}>Date</span>
          <input type="date" value={date} max={aujourdhuiIso()}
            onChange={(e) => { setDate(e.target.value); void charger(e.target.value, creneau); }}
            style={inputStyle} />
        </div>
        <div>
          <span style={lbl}>Passage</span>
          <select value={creneau} onChange={(e) => { setCreneau(e.target.value); void charger(date, e.target.value); }} style={inputStyle}>
            {t.creneaux.map((c) => <option key={c.code} value={c.code}>{c.label} ({c.heure})</option>)}
          </select>
        </div>
        <div>
          <span style={lbl}>Heure réelle de lecture</span>
          <input type="time" value={heure} onChange={(e) => setHeure(e.target.value)} style={inputStyle} />
        </div>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {remplies} / {t.lignes.length} points de relevé renseignés
          {t.lignes.some((l) => l.multiPoints)
            ? ` (${new Set(t.lignes.map((l) => l.code)).size} enceintes — une combinée compte deux points)`
            : ''}
        </span>
        <Bouton variante="primaire" type="button" disabled={estOccupe()} onClick={enregistrer}>
          <I n="check" /> Enregistrer la tournée
        </Bouton>
      </div>

      <div style={{ ...encart }}>
        Le relevé est <strong>manuel</strong>. Les colonnes <strong>Mini</strong> et <strong>Maxi</strong> sont
        celles d’un thermomètre mini/maxi : ce sont les seules qui couvrent la période écoulée depuis le passage
        précédent. Sans elles, une excursion survenue la nuit reste invisible — le registre l’écrit, il ne le
        suppose pas. Une enceinte qu’on ne relève pas se coche <strong>non relevée</strong> avec son motif ;
        laissée vide, elle n’est pas enregistrée et son passage reste <strong>manquant</strong>.
      </div>

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        {t.lignes.map((l) => (
          <LigneTournee
            key={l.cle} l={l} s={saisies[l.cle] ?? SAISIE_VIDE}
            onChange={(s) => setSaisies((prev) => ({ ...prev, [l.cle]: s }))}
          />
        ))}
      </div>

      {/* Socle UX (11/08/2026) — le même bouton, COLLANT en bas : après avoir
          renseigné quatorze enceintes, on ne remonte plus chercher le geste
          d'enregistrement en tête d'écran. */}
      <div className="ged-barre-collante" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {remplies} / {t.lignes.length} points renseignés
        </span>
        <span style={{ flex: 1 }} />
        <Bouton variante="primaire" type="button" disabled={estOccupe()} onClick={enregistrer}>
          <I n="check" /> Enregistrer la tournée
        </Bouton>
      </div>
    </div>
  );
}

/* ══ Le REGISTRE ═══════════════════════════════════════════════════════════ */

function Proposition({ p, ecartee, onFait }: {
  p: GedTempLignePropos; ecartee?: boolean; onFait: () => void;
}) {
  const smq = useGed((s) => s.smq);
  const settings = useGed((s) => s.settings);
  const [motif, setMotif] = useState('');
  const { estOccupe, run } = useBusy();
  return (
    <div style={{ padding: '11px 13px', borderTop: '1px solid var(--border-subtle)', display: 'grid', gap: 7 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13, color: 'var(--text-strong)' }}>{p.equipement}</strong>
        <span style={{ ...mono, color: 'var(--text-muted)' }}>{p.date} · {p.creneau}{p.heure ? ` · ${p.heure}` : ''}</span>
        {pill(STATUT_STYLE[p.verdict.statut] ?? STATUT_STYLE.non_jugeable)}
        {pill(COUV_STYLE[p.verdict.couverture] ?? COUV_STYLE.absente)}
        <span style={{ ...mono, color: 'var(--text-muted)' }}>provenance : {p.provenance}</span>
      </div>
      <div style={{ ...mono }}>
        actuelle {degre(p.valeur)} · mini {degre(p.valeurMin)} · maxi {degre(p.valeurMax)}
        {p.verdict.plage ? ` — plage ${p.verdict.plage.min} à ${p.verdict.plage.max} °C` : ''}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>{p.verdict.motif}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
        <strong>Proposition</strong> — {p.proposition.motif}
        {p.proposition.gravite ? ` (gravité proposée : ${p.proposition.gravite})` : ''}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        {p.duree
          ? `Durée : l’excursion est postérieure au dernier relevé conforme du ${p.duree.depuis}, soit AU PLUS `
            + `${p.duree.heures} h. Borne supérieure, pas une mesure : un mini/maxi dit qu’une température a été `
            + 'atteinte, jamais quand ni combien de temps.'
          : 'Durée : aucun relevé conforme antérieur — l’excursion n’a pas de point de départ connu.'}
      </div>
      {ecartee && p.ecartement && (
        <div style={{ ...encart }}>
          Écartée{p.ecartement.par ? ` par ${userName(settings, p.ecartement.par)}` : ''}
          {p.ecartement.le ? ` le ${fmtDate(p.ecartement.le)}` : ''} — {p.ecartement.motif}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text" value={motif} onChange={(e) => setMotif(e.target.value)}
          placeholder={ecartee ? 'Motif de la reprise (10 caractères minimum)' : 'Motif si vous écartez (10 caractères minimum)'}
          style={{ ...inputStyle, flex: 1, minWidth: 260 }}
        />
        {ecartee ? (
          <Bouton taille="sm" type="button" disabled={estOccupe() || motif.trim().length < 10} onClick={() => run('reprendre', async () => {
              if (await smq(`ged/releves-temp/${p.releveId}/nc/reprendre`, { motif })) onFait();
            })}>
            Remettre à l’examen
          </Bouton>
        ) : (
          <>
            <ConfirmButton
              label="Ouvrir la non-conformité" variant="primary"
              question={`Ouvrir une non-conformité pour ${p.equipement} — relevé du ${p.date} ?`}
              onConfirm={async () => { if (await smq(`ged/releves-temp/${p.releveId}/nc`, {})) onFait(); }}
            />
            <Bouton taille="sm" type="button" disabled={estOccupe() || motif.trim().length < 10} onClick={() => run('ecarter', async () => {
                if (await smq(`ged/releves-temp/${p.releveId}/nc/ecarter`, { motif })) onFait();
              })}>
              Écarter
            </Bouton>
          </>
        )}
      </div>
    </div>
  );
}

function Registre({ etat, recharger }: { etat: GedTempEtat; recharger: (avecEcartees: boolean) => void }) {
  const r = etat.recapitulatif;
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Tuile n={r.enceintes} label="Enceintes suivies" aide="Enceintes inscrites à la tournée. Le périmètre est déclaré sur la fiche, jamais déduit d’un libellé." />
        {r.points !== r.enceintes && (
          <Tuile n={r.points} label="Points de relevé" aide="Une enceinte peut être surveillée sur PLUSIEURS points, à plages distinctes — un congélateur combiné a un compartiment congélateur et un compartiment réfrigérateur. Chaque point a son propre relevé, son propre verdict et son propre manquant : deux points, deux relevés attendus à chaque passage." />
        )}
        <Tuile n={r.manquants} label="Passages manquants" ton={r.manquants ? 'alerte' : undefined}
          aide="Ce qui aurait dû être relevé et ne l’a pas été. DÉRIVÉ à la lecture, jamais stocké : changer le rythme change ce constat sur les jours déjà passés." />
        <Tuile n={r.franchissements} label="Franchissements" ton={r.franchissements ? 'alerte' : undefined}
          aide="Relevés hors de la plage de conservation de leur enceinte." />
        <Tuile n={r.propositions} label="NC à trancher" ton={r.propositions ? 'attention' : undefined}
          aide="Propositions de non-conformité ouvertes. Rien n’est créé d’office : la décision est signée." />
        <Tuile n={r.nonReleves} label="Non relevées" ton={r.nonReleves ? 'attention' : 'sourd'}
          aide="Enceintes explicitement déclarées non relevées, avec motif. Un constat, pas un trou." />
        <Tuile n={r.sansExtremes} label="Sans mini/maxi" ton={r.sansExtremes ? 'attention' : 'sourd'}
          aide="Relevés où seule la valeur du moment a été notée : la période écoulée depuis le passage précédent n’est PAS couverte." />
        <Tuile n={r.sansPlage} label="Sans plage" ton={r.sansPlage ? 'attention' : 'sourd'}
          aide="Enceintes sans plage de conservation fixée : le relevé s’enregistre, le franchissement n’est pas jugeable. Aucune plage n’est inventée." />
      </div>

      {!r.registreEnVigueurDepuis && (
        <div style={{ ...encart }}>
          ⚠️ <strong>Aucune date d’entrée en vigueur n’est fixée</strong> pour ce registre :
          tant qu’elle manque, <strong>aucun passage manquant n’est calculé</strong>. Sans cette borne, le
          manquant remonterait jusqu’à la création de chaque équipement et afficherait des années de relevés
          « jamais faits » qui n’ont jamais été demandés. À régler dans le paramétrage (RQ).
        </div>
      )}
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Rythme en vigueur : {etat.params.creneaux.map((c) => `${c.label} ${c.heure}`).join(' · ')}
        {' '}— tolérance {etat.params.toleranceHeures} h. {etat.calendrier}
      </div>

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '10px 13px', fontWeight: 700, fontSize: 13, color: 'var(--text-strong)' }}>
          Propositions de non-conformité ({etat.propositions.length})
        </div>
        {etat.propositions.length
          ? etat.propositions.map((p) => <Proposition key={p.releveId} p={p} onFait={() => recharger(etat.ecarteesDemandees)} />)
          : (
            <div style={{ padding: '10px 13px', borderTop: '1px solid var(--border-subtle)', fontSize: 12.5, color: 'var(--text-muted)' }}>
              Aucune proposition ouverte. ⚠️ Cela ne veut pas dire « aucune excursion » : une enceinte sans plage
              n’est pas jugeable, et un passage manquant n’est pas un relevé conforme.
            </div>
          )}
      </div>

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '10px 13px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-strong)' }}>Propositions écartées</span>
          <Bouton taille="sm" type="button" onClick={() => recharger(!etat.ecarteesDemandees)}>
            {etat.ecarteesDemandees ? 'Masquer' : 'Afficher'}
          </Bouton>
        </div>
        {etat.ecarteesDemandees && (etat.ecartees.length
          ? etat.ecartees.map((p) => <Proposition key={p.releveId} p={p} ecartee onFait={() => recharger(true)} />)
          : <div style={{ padding: '10px 13px', borderTop: '1px solid var(--border-subtle)', fontSize: 12.5, color: 'var(--text-muted)' }}>
            Aucune proposition écartée.
          </div>)}
      </div>

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '10px 13px', fontWeight: 700, fontSize: 13, color: 'var(--text-strong)' }}>
          État des enceintes
        </div>
        {etat.enceintes.map((e) => (
          <div key={e.cle} style={{ padding: '10px 13px', borderTop: '1px solid var(--border-subtle)', display: 'grid', gap: 5 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 13, color: 'var(--text-strong)' }}>{e.code}</strong>
              {/* Un combiné a DEUX lignes ici, une par compartiment : les fondre
                  en une seule masquerait un compartiment jamais relevé. */}
              {e.pointLibelle
                ? <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-body)' }}>{e.pointLibelle}</span>
                : null}
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{e.nom} — {e.localisation || 'sans localisation'}</span>
              <span style={{ ...mono, color: e.jugeable ? 'var(--text-body)' : 'var(--amber-600)' }}>
                {e.jugeable && e.plage ? `${e.plage.min} à ${e.plage.max} °C` : 'plage non fixée'}
              </span>
              <span style={{ flex: 1 }} />
              {e.manquants > 0 && <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--red-600)' }}>{e.manquants} manquant(s)</span>}
              {e.horsPlage > 0 && <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--red-600)' }}>{e.horsPlage} hors plage</span>}
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{e.releves} relevé(s)</span>
            </div>
            {e.dernier ? (
              <div style={{ ...mono, color: 'var(--text-muted)' }}>
                dernier {e.dernier.date} ({e.dernier.creneau}) : {degre(e.dernier.valeur)} · mini {degre(e.dernier.valeurMin)}
                {' '}· maxi {degre(e.dernier.valeurMax)} — {STATUT_STYLE[e.dernier.statut]?.label ?? e.dernier.statut}
                {' '}· couverture {COUV_STYLE[e.dernier.couverture]?.label ?? e.dernier.couverture}
                {' '}· provenance {e.dernier.provenance}
              </div>
            ) : (
              <div style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>
                Aucun relevé enregistré depuis l’entrée en vigueur du registre.
              </div>
            )}
            {!e.jugeable && (
              <div style={{ fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.5 }}>
                Aucune plage de conservation fixée : les relevés sont enregistrés, le franchissement n’est PAS
                jugeable. {e.plageAVerifier ? `Question conservée : ${e.plageAVerifier}` : 'À trancher sur la fiche de l’équipement.'}
              </div>
            )}
            {e.manquantsRecents.length > 0 && (
              <div style={{ ...mono, color: 'var(--red-600)' }}>
                manquants récents : {e.manquantsRecents.map((m) => `${m.date} ${m.creneau}`).join(' · ')}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══ Le PARAMÉTRAGE ════════════════════════════════════════════════════════
 *
 * ── Ce que la RQ règle ici, et rien de plus ────────────────────────────────
 *
 * Le rythme (les passages attendus dans une journée), la tolérance, la date
 * d'entrée en vigueur du registre, et le périmètre (quelles enceintes sont
 * inscrites à la tournée). Jusqu'au 08/08/2026 les trois se réglaient par la
 * route ou par un script : un dispositif dont le réglage n'est atteignable
 * qu'en ligne de commande n'est pas réglé, il est subi.
 *
 * ── Le piège, et le garde-fou ──────────────────────────────────────────────
 *
 * Le manquant est DÉRIVÉ, jamais stocké : changer le rythme change ce que le
 * registre dit. Sans précaution, passer de deux passages à un seul ferait
 * disparaître d'un coup la moitié des manquants de TOUS les jours passés — y
 * compris ceux d'hier, qui étaient bel et bien dus.
 *
 * Le rythme porte donc une DATE D'EFFET. Les jours antérieurs restent jugés
 * sous le rythme qui était alors en vigueur (`params.anterieurs`), et le
 * serveur REFUSE une date d'effet passée. L'écran dit, avant l'enregistrement,
 * à partir de quand le changement s'applique et sur quel périmètre.
 *
 * ⚠️ La marge en °C et le poste de référence du calendrier ne se règlent PAS
 * ici : ce sont des décisions d'un autre ordre (l'une touche au verdict de
 * relevés déjà pris, l'autre au calendrier d'ouverture du laboratoire). Ils
 * sont affichés en lecture et renvoyés INCHANGÉS à l'enregistrement — les
 * omettre du corps les remettrait à leur valeur par défaut en silence. */

type BrouillonCreneau = { code: string; label: string; heure: string };

function Parametrage({ etat, recharger }: { etat: GedTempEtat; recharger: () => void }) {
  const smq = useGed((s) => s.smq);
  const showToast = useGed((s) => s.showToast);
  const { estOccupe, run } = useBusy();
  const aujourdhui = aujourdhuiIso();

  const [creneaux, setCreneaux] = useState<BrouillonCreneau[]>(
    () => etat.params.creneaux.map((c) => ({ code: c.code, label: c.label, heure: c.heure })),
  );
  const [tolerance, setTolerance] = useState(String(etat.params.toleranceHeures));
  const [debut, setDebut] = useState(etat.params.debut);
  const [actif, setActif] = useState(etat.params.actif);
  const [effetLe, setEffetLe] = useState(aujourdhui);
  const [candidat, setCandidat] = useState('');

  const rythmeInitial = useMemo(
    () => JSON.stringify(etat.params.creneaux.map((c) => [c.code, c.label, c.heure])),
    [etat.params.creneaux],
  );
  const rythmeCourant = JSON.stringify(creneaux.map((c) => [c.code.trim(), c.label.trim(), c.heure]));
  const rythmeChange = rythmeCourant !== rythmeInitial
    || Number(tolerance) !== etat.params.toleranceHeures;
  const debutAvance = !!etat.params.debut && !!debut && debut > etat.params.debut;

  const enregistrer = () => run('params', async () => {
    const propres = creneaux
      .map((c) => ({ code: c.code.trim(), label: c.label.trim() || c.code.trim(), heure: c.heure }))
      .filter((c) => c.code);
    const ok = await smq('ged/releves-temp/params', {
      releve: {
        // ⚠️ Le corps porte le paramétrage ENTIER. Le serveur relit chaque clé
        // et retombe sur le défaut sur une clé absente : omettre `margeC` ou
        // `posteReference` les remettrait à zéro en silence.
        actif,
        creneaux: propres,
        debut,
        toleranceHeures: Number(tolerance),
        posteReference: etat.params.posteReference,
        margeC: etat.params.margeC,
        anterieurs: etat.params.anterieurs,
        effetLe,
      },
    });
    if (ok) {
      showToast(rythmeChange
        ? `Rythme enregistré, applicable à partir du ${effetLe}. Les jours antérieurs restent jugés sous le rythme précédent.`
        : 'Paramétrage enregistré.');
      recharger();
    }
  });

  const inscrire = (codes: string[], oui: boolean) => run('perimetre', async () => {
    const ok = await smq('ged/releves-temp/perimetre', { codes, inscrire: oui });
    if (ok) { showToast(oui ? 'Enceinte inscrite à la tournée.' : 'Enceinte retirée de la tournée.'); recharger(); }
  });

  const champ = (i: number, k: keyof BrouillonCreneau, largeur: number, type = 'text') => (
    <input
      type={type} value={creneaux[i]?.[k] ?? ''}
      aria-label={`Passage ${i + 1} — ${k}`}
      onChange={(e) => setCreneaux(creneaux.map((c, j) => (j === i ? { ...c, [k]: e.target.value } : c)))}
      style={{ ...inputStyle, width: largeur }}
    />
  );

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* ── LE RYTHME ─────────────────────────────────────────────────── */}
      <div style={{ ...card, padding: 14, display: 'grid', gap: 10 }}>
        <strong style={{ fontSize: 13, color: 'var(--text-strong)' }}>Le rythme — les passages attendus chaque jour</strong>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
          Deux par défaut, début et fin de journée : c’est ce que chiffre <em>OPP-ERA</em> §3.1, la seule
          source du dossier qui chiffre un rythme. C’est un arbitrage du laboratoire, pas une constante du
          logiciel. Le <strong>code</strong> d’un passage est écrit sur chaque relevé : le renommer sans
          reprise rendrait orphelins les relevés déjà pris — ajoutez plutôt un passage et retirez l’ancien.
        </p>
        {creneaux.map((c, i) => (
          <div key={`${i}-${c.code}`} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {champ(i, 'code', 110)}
            {champ(i, 'label', 200)}
            {champ(i, 'heure', 110, 'time')}
            <Bouton taille="sm" type="button" onClick={() => setCreneaux(creneaux.filter((_, j) => j !== i))}>
              <I n="x" s={11} /> Retirer
            </Bouton>
          </div>
        ))}
        <div>
          <Bouton taille="sm" type="button" onClick={() => setCreneaux([...creneaux, { code: '', label: '', heure: '12:00' }])}>
            Ajouter un passage
          </Bouton>
        </div>
        {!creneaux.filter((c) => c.code.trim()).length && (
          <div style={{ ...encart }}>
            ⚠️ <strong>Aucun passage</strong> : zéro attendu, donc zéro manquant — un dispositif muet qui se
            croit armé. Le serveur retombera sur les deux passages sourcés plutôt que d’enregistrer ce silence.
          </div>
        )}

        <div style={{ display: 'flex', gap: 14, alignItems: 'end', flexWrap: 'wrap' }}>
          <div>
            <span style={lbl}>Tolérance (heures)</span>
            <input type="number" min={0} max={72} value={tolerance}
              onChange={(e) => setTolerance(e.target.value)} style={{ ...inputStyle, width: 110 }} />
          </div>
          <div>
            <span style={lbl}>Le rythme s’applique à partir du</span>
            <input type="date" value={effetLe} min={aujourdhui}
              onChange={(e) => setEffetLe(e.target.value)} style={inputStyle} />
          </div>
        </div>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Un passage de 17 h n’est pas manquant à 17 h 01. Entre l’heure du passage et la fin de la tolérance,
          l’état est <strong>attendu</strong>, qui n’est pas <strong>manquant</strong>.
        </p>

        {/* ── CE QUE LE CHANGEMENT PRODUIT — dit AVANT d'enregistrer ──── */}
        {rythmeChange && (
          <div style={{ ...encart }}>
            <strong>Ce que ce changement produit.</strong> À partir du <strong>{effetLe}</strong>, chaque jour
            ouvré attendra <strong>{creneaux.filter((c) => c.code.trim()).length}</strong> passage(s) par point
            de relevé, sur les <strong>{etat.recapitulatif.points}</strong> point(s) de
            {' '}{etat.recapitulatif.enceintes} enceinte(s) inscrite(s) — et sur eux seuls.
            <br />
            <strong>Les jours antérieurs ne bougent pas.</strong> Ils restent jugés sous le rythme qui était
            alors en vigueur ({etat.params.creneaux.map((c) => `${c.label} ${c.heure}`).join(' · ')}) : les
            {' '}{etat.recapitulatif.manquants} passage(s) actuellement manquant(s) ne peuvent pas disparaître
            du fait de ce réglage. Une date d’effet passée est refusée par le serveur, pour cette raison même.
          </div>
        )}
      </div>

      {/* ── L'ENTRÉE EN VIGUEUR ───────────────────────────────────────── */}
      <div style={{ ...card, padding: 14, display: 'grid', gap: 10 }}>
        <strong style={{ fontSize: 13, color: 'var(--text-strong)' }}>L’entrée en vigueur du registre</strong>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
          Tant qu’elle n’est pas posée, <strong>aucun passage manquant n’est calculé</strong>. Sans cette
          borne, le manquant remonterait jusqu’à la création de chaque équipement et afficherait des années
          de relevés « jamais faits » qui n’ont jamais été demandés.
        </p>
        <div style={{ display: 'flex', gap: 14, alignItems: 'end', flexWrap: 'wrap' }}>
          <div>
            <span style={lbl}>Registre en vigueur depuis</span>
            <input type="date" value={debut} onChange={(e) => setDebut(e.target.value)} style={inputStyle} />
          </div>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 12.5 }}>
            <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)} />
            registre actif
          </label>
        </div>
        {debutAvance && (
          <div style={{ ...encart }}>
            ⚠️ Vous <strong>avancez</strong> l’entrée en vigueur du {etat.params.debut} au {debut} : les
            passages attendus entre ces deux dates ne seront plus comptés comme manquants. Ce n’est pas une
            correction de calcul, c’est un <strong>retrait de constat</strong> — il est inscrit au journal
            chaîné, avec son auteur.
          </div>
        )}
        {!actif && (
          <div style={{ ...encart }}>
            ⚠️ Registre <strong>inactif</strong> : plus aucun passage n’est attendu, donc plus aucun manquant
            n’est calculé. Les relevés déjà pris restent lisibles, mais le registre cesse de dire ce qui
            manque.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <Bouton variante="primaire" type="button" disabled={estOccupe()} onClick={enregistrer}>
          <I n="check" /> Enregistrer le paramétrage
        </Bouton>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Réservé au RQ. Chaque modification est inscrite au journal chaîné, clé par clé, avec sa date d’effet.
        </span>
      </div>

      {/* ── LE PÉRIMÈTRE ──────────────────────────────────────────────── */}
      <div style={{ ...card, padding: 14, display: 'grid', gap: 10 }}>
        <strong style={{ fontSize: 13, color: 'var(--text-strong)' }}>
          Le périmètre — {etat.recapitulatif.enceintes} enceinte(s) inscrite(s)
        </strong>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
          Le périmètre est <strong>déclaré</strong>, jamais déduit d’un libellé : « Réfrigérateur » dans un nom
          ne prouve rien, et la nature <em>métrologie</em> couvre aussi les centrifugeuses, les agitateurs et
          les micropipettes. Retirer une enceinte ne supprime aucun relevé déjà pris ; elle cesse simplement
          d’être attendue.
        </p>
        <div style={{ display: 'grid' }}>
          {/* ⚠️ Une ligne par ENCEINTE, pas par point de surveillance : le
              périmètre s'inscrit et se retire par enceinte. Filtrer sur
              `!pointCode` aurait fait disparaître `CONG-03` de cette liste —
              c'est la seule du parc dont TOUS les points portent un code. */}
          {etat.enceintes.filter((e, i, tab) => tab.findIndex((x) => x.code === e.code) === i).map((e) => (
            <div key={e.code} style={{
              display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
              padding: '6px 0', borderTop: '1px solid var(--border-subtle)',
            }}>
              <span style={{ ...mono, fontWeight: 700 }}>{e.code}</span>
              <span style={{ fontSize: 12.5 }}>{e.nom}</span>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{e.localisation || 'sans localisation'}</span>
              <span style={{ flex: 1 }} />
              <ConfirmButton
                label="Retirer de la tournée" variant="outline"
                question={`Retirer ${e.code} de la tournée ? Ses ${e.releves} relevé(s) sont conservés ; l’enceinte cesse d’être attendue à chaque passage.`}
                onConfirm={async () => { await inscrire([e.code], false); }}
              />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap', marginTop: 4 }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <span style={lbl}>Inscrire une enceinte</span>
            {/* `Selecteur` plutôt qu'un `<select>` natif : la frappe d'un
                `<select>` ne reconnaît que le DÉBUT du libellé, donc le code —
                on cherche une enceinte aussi souvent par son nom. */}
            <Selecteur value={candidat} onChange={setCandidat} aria-label="Inscrire une enceinte"
              style={{ ...inputStyle, width: '100%' }}>
              <option value="">— choisir un équipement —</option>
              {etat.candidats.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} — {c.nom}{c.categorie ? ` (${c.categorie})` : ''}
                  {c.plageTexte ? ` · plage écrite : ${c.plageTexte}` : ''}
                </option>
              ))}
            </Selecteur>
          </div>
          <Bouton taille="sm" type="button" disabled={estOccupe() || !candidat} onClick={() => { const c = candidat; setCandidat(''); void inscrire([c], true); }}>
            Inscrire à la tournée
          </Bouton>
        </div>
        {!etat.candidatsDemandes && (
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
            La liste des équipements inscriptibles n’a pas été chargée — rouvrez cet onglet.
          </p>
        )}
      </div>

      {/* ── CE QUI NE SE RÈGLE PAS ICI ────────────────────────────────── */}
      <div style={{ ...card, padding: 14, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
        <strong style={{ color: 'var(--text-strong)' }}>Ce qui ne se règle pas ici, et pourquoi.</strong>
        {' '}La <strong>marge de {etat.params.margeC} °C</strong> n’absorbe que la résolution du thermomètre :
        la modifier changerait le verdict de relevés <em>déjà pris</em>, ce qui est le contraire d’un réglage
        de rythme. Le <strong>poste de référence du calendrier</strong>
        {etat.params.posteReference ? <> (<em>{etat.params.posteReference}</em>)</> : <> n’est pas désigné</>}
        {' '}: il dit quels jours le laboratoire tourne, et cette question appartient au calendrier
        d’ouverture, pas à ce registre. {etat.calendrier} Les <strong>plages de conservation</strong> se
        posent sur la fiche de chaque équipement, une fiche à la fois — aucune n’est inventée ici.
      </div>
    </div>
  );
}

/* ══ L'écran ═══════════════════════════════════════════════════════════════ */

export function RelevesTemperature() {
  const session = useGed((s) => s.session);
  const [onglet, setOnglet] = useState<'tournee' | 'registre' | 'parametrage'>('tournee');
  // Un sous-onglet = une arrivée : on remonte (règle du 24/08/2026, cf. le crochet).
  useRemonteEnHaut(onglet);
  const [etat, setEtat] = useState<GedTempEtat | null>(null);
  const [erreur, setErreur] = useState('');
  // Le paramétrage est réservé au RQ côté MOTEUR — c'est là que ça compte.
  // L'onglet est masqué pour les autres afin de ne pas offrir un écran qui
  // refuserait à l'enregistrement, mais c'est du confort, pas une garde.
  const peutParc = peutGererParc(session.roles);

  const lire = useCallback(async (avecEcartees: boolean, avecCandidats = false): Promise<GedTempEtat | null> => {
    const q = new URLSearchParams();
    if (avecEcartees) q.set('ecartees', '1');
    // Les candidats au périmètre ne sont chargés que pour l'onglet qui s'en
    // sert : le registre n'a pas à traîner 200 fiches à chaque lecture.
    if (avecCandidats) q.set('candidats', '1');
    const s = q.toString();
    const r = await fetch(`/api/ged/ged/releves-temp${s ? `?${s}` : ''}`, { credentials: 'include' });
    return r.ok ? ((await r.json()) as GedTempEtat) : null;
  }, []);

  // Un 404 « Route non autorisée » du proxy afficherait sinon un registre vide,
  // c'est-à-dire « rien à signaler » — le mode d'échec que tout ce module
  // existe pour rendre impossible. L'écran dit donc qu'il n'a PAS pu lire.
  const ECHEC = 'Le registre des relevés n’a pas pu être chargé. Rien ne peut être conclu de cet écran.';

  const charger = useCallback(async (avecEcartees: boolean, avecCandidats = false) => {
    const d = await lire(avecEcartees, avecCandidats);
    if (d) { setEtat(d); setErreur(''); } else setErreur(ECHEC);
  }, [lire]);

  useEffect(() => {
    let vivant = true;
    void lire(false).then((d) => {
      if (!vivant) return;
      if (d) { setEtat(d); setErreur(''); } else setErreur(ECHEC);
    });
    return () => { vivant = false; };
  }, [lire]);

  const bandeau = useMemo(() => (
    <div style={{ ...encart, background: 'var(--cyan-50)', borderInlineStartColor: 'var(--cyan-700)' }}>
      Le suivi est <strong>manuel</strong>, au thermomètre, <strong>deux fois par jour</strong>
      {' '}(<em>OPP-ERA</em> §3.1). <strong>Aucune sonde n’est installée</strong> : le registre porte la
      provenance de chaque mesure (<em>manuel</em> / <em>sonde</em> + identifiant du capteur) pour que
      l’ingestion future écrive ici sans réécrire le registre — mais rien, dans ce produit, ne simule une
      sonde. Entre deux passages, seule une lecture <strong>mini/maxi</strong> couvre la période écoulée.
    </div>
  ), []);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <H1 eyebrow={<RefISO code="6.3.3" />} title="Relevés de température des enceintes" />
      {bandeau}
      <div>
        <Segment ariaLabel="Rubrique des relevés" taille="md" valeur={onglet}
          onChange={(v) => {
            const o = v as typeof onglet;
            setOnglet(o);
            if (o === 'registre') void charger(false);
            if (o === 'parametrage') void charger(false, true);
          }}
          options={[
            { valeur: 'tournee', libelle: 'Saisir la tournée' },
            { valeur: 'registre', libelle: 'Registre & franchissements' },
            ...(peutParc ? [{ valeur: 'parametrage', libelle: <><I n="gear" s={13} /> Paramétrage</> }] : []),
          ]} />
      </div>
      {erreur && (
        <div style={{ ...card, padding: 16, color: 'var(--red-600)', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ flex: '1 1 260px' }}>{erreur}</span>
          <Bouton taille="sm" type="button" onClick={() => { setErreur(''); void charger(false, onglet === 'parametrage'); }}>Réessayer</Bouton>
        </div>
      )}
      {onglet === 'tournee' && <Tournee onEnregistre={() => void charger(false)} />}
      {onglet === 'registre' && (etat
        ? <Registre etat={etat} recharger={(a) => void charger(a)} />
        : erreur ? null : <div style={card}><Skeleton rows={4} /></div>)}
      {/* Le journal du paramétrage — saisies rectifiées, réglages, périmètre
          s'écrivent sous « RELEVE-TEMP » sans autre accès à l'écran (audit
          traçabilité du 24/08/2026). */}
      {onglet === 'parametrage' && <div style={{ display: 'flex', justifyContent: 'flex-end' }}><BoutonJournal reference="RELEVE-TEMP" quoi="du paramétrage des relevés" /></div>}
      {onglet === 'parametrage' && (etat
        // ⚠️ La clé force le remontage après enregistrement : sans elle, les
        // champs garderaient le brouillon d'avant et l'écran montrerait un
        // réglage qui n'est plus celui de la base.
        ? <Parametrage key={JSON.stringify(etat.params)} etat={etat} recharger={() => void charger(false, true)} />
        : erreur ? null : <div style={card}><Skeleton rows={4} /></div>)}
    </div>
  );
}
